import { JobArrive, JobDeparture, JobPerform, OnTheWay } from './job.type';

import { CallRequest } from './call-request.type';
import { CompleteRoute } from './complete-route.type';
import { FlagStopPerform } from './flag-stop.type';
import { Fluids } from './fluids.type';
import { GpsReport } from './gps-report.type';
import { InspectionItemResult } from './inspection.type';
import { Logout } from './logout.type';
import { MessageResponse } from './message-response.type';
import { SendPredefinedMessage } from './send-predefined-message.type';
import { WaypointPerform } from './waypoint.type';
import { DispatchMessage } from './dispatch-message.type';
import { DisableDrivingMode } from './disable-driving-mode.type';
import { SendFreeFormMessage } from './send-free-form-message.type';

export type RouteStatusUpdate = {
  sessionId: string;
  retryCount: number;
  messageId: string;
  locationSentSinceLastUpdate: boolean;
  gpsReports: GpsReport[];
  jobArrivals: JobArrive[];
  jobPerforms: JobPerform[];
  jobDepartures: JobDeparture[];
  predefinedMessages: SendPredefinedMessage[];
  messageResponses: MessageResponse[];
  callRequests: CallRequest[];
  breakStart: GpsReport;
  breakEnd: GpsReport;
  preTripStart: GpsReport;
  preTripEnd: GpsReport;
  postTripStart: GpsReport;
  postTripEnd: GpsReport;
  preTripInspection: InspectionItemResult[];
  postTripInspection: InspectionItemResult[];
  customFormResults: InspectionItemResult[];
  fluids: Fluids;
  completeRoute: CompleteRoute;
  logout: Logout;
  flagStopPerforms: FlagStopPerform[];
  onTheWays: OnTheWay[];
  wayPointOnTheWays: WaypointPerform[];
  wayPointArrivals: WaypointPerform[];
  wayPointPerforms: WaypointPerform[];
  errorMessages: string[];
  pullOutPerform?: PullPerform;
  pullInPerform?: PullPerform;
  disabledDrivingModes?: DisableDrivingMode[];
  freeformMessages: SendFreeFormMessage[];
}

export type PullPerform = {
  performTime: Date;
  gpsReport: GpsReport;
}

export type RouteStatusUpdateResult = {
  loggedOutDate: Date;
  driverFirstName: string;
  driverLastName: string;
  vehicleNumber: string;
  routeNumber: number;
  needUpdateFromServer: boolean;
  desync?: boolean;
  messages?: DispatchMessage[]
}

export function concatStatusUpdates(route: RouteStatusUpdate, update: RouteStatusUpdate) {
  // Preserve old sessionId from pending updates
  // When app is killed and reopened, orchestrator sends pending updates BEFORE login
  // The old sessionId should be preserved so server can validate against the still-active route
  route.sessionId = update.sessionId || route.sessionId;

  route.callRequests = route.callRequests?.concat(update.callRequests || []) || [];
  route.gpsReports = route.gpsReports.concat(update.gpsReports);
  route.jobPerforms = route.jobPerforms.concat(update.jobPerforms);
  route.messageResponses = route.messageResponses.concat(update.messageResponses);
  route.predefinedMessages = route.predefinedMessages.concat(update.predefinedMessages);
  route.freeformMessages = route.freeformMessages.concat(update.freeformMessages);
  route.jobArrivals = route.jobArrivals.concat(update.jobArrivals);
  route.jobDepartures = route.jobDepartures.concat(update.jobDepartures);

  route.preTripInspection = route.preTripInspection.concat(update.preTripInspection);
  route.postTripInspection = route.postTripInspection.concat(update.postTripInspection);
  route.customFormResults = route.customFormResults.concat(update.customFormResults);

  route.flagStopPerforms = route.flagStopPerforms.concat(update.flagStopPerforms);
  route.onTheWays = route.onTheWays.concat(update.onTheWays);
  route.wayPointArrivals = route.wayPointArrivals.concat(update.wayPointArrivals);
  route.wayPointOnTheWays = route.wayPointOnTheWays.concat(update.wayPointOnTheWays);
  route.wayPointPerforms = route.wayPointPerforms.concat(update.wayPointPerforms);

  route.errorMessages = route.errorMessages.concat(update.errorMessages);

  route.disabledDrivingModes = route.disabledDrivingModes?.concat(update.disabledDrivingModes || []) || [];

  [ 'fluids', 'pullOutPerform', 'breakStart', 'breakEnd', 'preTripStart', 'preTripEnd', 'postTripStart', 'postTripEnd', 'pullInPerform', 'completeRoute', 'logout' ]
  .forEach(prop => {
    if (update[prop] && JSON.stringify(route[prop] ?? '') !== JSON.stringify[update[prop] ?? '']) {
      route[prop] = update[prop];
    }
  });

  route.retryCount = update.retryCount;

  return route;
}

export function createNewStatusUpdate(sessionId: string = '') {
  return {
    sessionId,
    retryCount: 0,
    messageId: null,
    locationSentSinceLastUpdate: true,
    callRequests: [],
    customFormResults: [],
    gpsReports: [],
    jobPerforms: [],
    jobDepartures: [],
    messageResponses: [],
    predefinedMessages: [],
    jobArrivals: [],
    fluids: null,
    breakStart: null,
    breakEnd: null,
    preTripStart: null,
    preTripEnd: null,
    preTripInspection: [],
    postTripStart: null,
    postTripEnd: null,
    postTripInspection: [],
    completeRoute: null,
    logout: null,
    flagStopPerforms: [],
    onTheWays: [],
    wayPointOnTheWays: [],
    wayPointArrivals: [],
    wayPointPerforms: [],
    errorMessages: [],
    pullOutPerform: null,
    pullInPerform: null,
    disabledDrivingModes: [],
    freeformMessages: [],
  };
}
